/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DeviceModel } from '../../../../core/device';
import { Locale, LocaleKeys } from '../../../../core/locale';
import type { TivoPayloadAction } from '../../../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../../../core/utils';
import {
    onKeyDownList,
    onKeyUpList,
    onMouseOutList,
    onMouseOverList,
} from '../../../list/list.reducers';
import { showGuideScreen, showUserPreferencesScreen } from '../../../screens/screens.reducers';
import { IAutoplayNextEpisodeSelectedOption } from './IAutoplayNextEpisodeSelectedOption';
import {
    setAutoplayNextEpisodeListAction,
    setSelectedAutoplayNextEpisodeOptionAction,
} from './userprefautoplaynextepisode.actions';
import {
    SET_AUTOPLAY_NEXT_EPISODE_OPTIONS_LIST,
    SET_AUTOPLAY_NEXT_EPISODE_SELECTED_OPTION,
} from './userprefautoplaynextepisode.types';

interface IAutoplayNextEpisode {
    optionsList: Array<IAutoplayNextEpisodeSelectedOption>;
    selectedOption?: IAutoplayNextEpisodeSelectedOption;
}

const initialState: IAutoplayNextEpisode = {
    optionsList: [],
};

const userPrefAutoplayNextEpisodeReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IAutoplayNextEpisode>>{},
) => {
    switch (type) {
        case SET_AUTOPLAY_NEXT_EPISODE_OPTIONS_LIST:
            return {
                ...state,
                optionsList: payload.optionsList,
            };
        case SET_AUTOPLAY_NEXT_EPISODE_SELECTED_OPTION:
            return {
                ...state,
                selectedOption: payload.selectedOption,
            };
        default:
            return state;
    }
};

export const setAutoplayNextEpisodeOptionsList = () => (dispatch: Function) => {
    const selectedAutoplayOption = DeviceModel.getAppSettings().isAutoplayNextEpisodeEnabled()
        ? Locale.getString(LocaleKeys.SETTINGS_AUTOPLAY_NEXT_EPISODE_ON)
        : Locale.getString(LocaleKeys.SETTINGS_AUTOPLAY_NEXT_EPISODE_OFF);
    const settingsItemList: Array<IAutoplayNextEpisodeSelectedOption> = [
        {
            value: Locale.getString(LocaleKeys.SETTINGS_AUTOPLAY_NEXT_EPISODE_ON),
            isSelected:
                selectedAutoplayOption ===
                Locale.getString(LocaleKeys.SETTINGS_AUTOPLAY_NEXT_EPISODE_ON),
            preview: Locale.getString(
                LocaleKeys.SETTINGS_USER_PREFERENCES_AUTOPLAY_NEXT_EPISODE_PREVIEW_TEXT,
            ),
        },
        {
            value: Locale.getString(LocaleKeys.SETTINGS_AUTOPLAY_NEXT_EPISODE_OFF),
            isSelected:
                selectedAutoplayOption ===
                Locale.getString(LocaleKeys.SETTINGS_AUTOPLAY_NEXT_EPISODE_OFF),
            preview: Locale.getString(
                LocaleKeys.SETTINGS_USER_PREFERENCES_AUTOPLAY_NEXT_EPISODE_PREVIEW_TEXT,
            ),
        },
    ];
    dispatch(setAutoplayNextEpisodeListAction(settingsItemList));
    dispatch(
        setSelectedAutoplayNextEpisodeOptionAction(
            settingsItemList.filter(
                (value: IAutoplayNextEpisodeSelectedOption) => value.isSelected,
            )[0],
        ),
    );
};

export const resetAutoplayNextEpisodeOptionsList = () => (dispatch: Function) => {
    dispatch(setSelectedAutoplayNextEpisodeOptionAction());
};

/**
 * Below reducers are related to operations on key events.
 */

export const onKeyBackAutoplayNextEpisode = () => (dispatch: Function) => {
    dispatch(setSelectedAutoplayNextEpisodeOptionAction());
    dispatch(showUserPreferencesScreen());
};

export const onKeyDownAutoplayNextEpisode = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.AUTOPLAY_NEXT_EPISODE]: { position },
        },
        userPrefAutoplayNextEpisode: { optionsList },
    } = getState();
    if (position < optionsList.length - 1) {
        dispatch(setSelectedAutoplayNextEpisodeOptionAction(optionsList[position + 1]));
        dispatch(onKeyDownList(ListTypes.AUTOPLAY_NEXT_EPISODE));
    }
};

export const onKeyGuideAutoplayNextEpisode = () => (dispatch: Function) => {
    dispatch(setSelectedAutoplayNextEpisodeOptionAction());
    dispatch(showGuideScreen());
};

export const onKeyOkAutoplayNextEpisode = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.AUTOPLAY_NEXT_EPISODE]: { position },
        },
        userPrefAutoplayNextEpisode: { optionsList },
    } = getState();
    const selectedOption = optionsList[position];
    if (selectedOption.value === Locale.getString(LocaleKeys.SETTINGS_AUTOPLAY_NEXT_EPISODE_ON)) {
        DeviceModel.getAppSettings().setIsAutoplayNextEpisodeEnabled(true);
    } else {
        DeviceModel.getAppSettings().setIsAutoplayNextEpisodeEnabled(false);
    }
    selectedOption.isSelected = true;
    dispatch(setSelectedAutoplayNextEpisodeOptionAction(selectedOption));
    dispatch(showUserPreferencesScreen());
};

export const onKeyUpAutoplayNextEpisode = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.AUTOPLAY_NEXT_EPISODE]: { position },
        },
        userPrefAutoplayNextEpisode: { optionsList },
    } = getState();
    if (position > 0) {
        dispatch(setSelectedAutoplayNextEpisodeOptionAction(optionsList[position - 1]));
        dispatch(onKeyUpList(ListTypes.AUTOPLAY_NEXT_EPISODE));
    }
};

export const onMouseClickAutoplayNextEpisode = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    if (value.includes(ListTypes.AUTOPLAY_NEXT_EPISODE)) dispatch(onKeyOkAutoplayNextEpisode());
};

export const onMouseOutAutoplayNextEpisode = () => (dispatch: Function) => {
    dispatch(onMouseOutList());
};

export const onMouseOverAutoplayNextEpisode = () => (dispatch: Function, getState: Function) => {
    dispatch(onMouseOverList());
    const {
        list: {
            [ListTypes.AUTOPLAY_NEXT_EPISODE]: { position },
        },
        userPrefAutoplayNextEpisode: { optionsList },
    } = getState();
    dispatch(setSelectedAutoplayNextEpisodeOptionAction(optionsList[position]));
};

export default userPrefAutoplayNextEpisodeReducer;
